"use client";

import type { JSX } from "react";
import { useState } from "react";
import { Check, Share2 } from "lucide-react";

interface ShareButtonProps {
  title: string;
  url: string;
}

// Always visible: uses the native share sheet where available (mostly
// mobile), otherwise falls back to copying the link — most desktop
// browsers don't implement navigator.share, so this can't be conditional
// on it without leaving desktop readers with no share affordance at all.
export function ShareButton({ title, url }: ShareButtonProps): JSX.Element {
  const [copied, setCopied] = useState(false);

  const handleShare = async (): Promise<void> => {
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
        return;
      } catch {
        return; // User cancelled — don't fall through to the clipboard copy.
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable/denied — no-op.
    }
  };

  return (
    <button
      type="button"
      onClick={handleShare}
      className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1 text-xs text-gray-600 transition-colors hover:border-indigo-300 hover:text-indigo-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600 focus-visible:ring-offset-2"
    >
      {copied ? (
        <>
          <Check className="h-3.5 w-3.5" aria-hidden />
          Copied!
        </>
      ) : (
        <>
          <Share2 className="h-3.5 w-3.5" aria-hidden />
          Share
        </>
      )}
    </button>
  );
}
